import React, { useCallback, useEffect, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import { Drawer } from '@mui/material';
import postService from '../../Services/post-service';
import swal from 'sweetalert';
import ImageUploader from '../Dropdown';
import getServices from '../../Services/get-service';
import putServices from '../../Services/put-service';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
const drawerWidth = 650;
const steps = ['Asset Details', 'Pricing & Cost', 'Review & Summary'];
const downPaymentOptions = ['10', '20', '30', '40', '50'];
const markupOptions = ['10', '15', '20', '25'];
const tenureOptions = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '12'];
const preferredTenureOptions = ['3', '6', '9', '12'];

function AddAssets({open2, handleClose2, onCreateCategory, assetToEdit, recommendationToApprove, onSaved}) {
      
  const [images, setImages] = useState([]);
  const [retainedImages, setRetainedImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  
  const [name, setName] = useState('')
  const [assetType, setAssetType] = useState('')
  const [description, setDescription] = useState('')
  const [unitPrice, setUnitPrice] = useState('')
  const [totalUnits, setTotalUnits] = useState('')
  const [markupRate, setMarkupRate] = useState('15')
  const [minDeposit, setMinDeposit] = useState('30')
  const [acceptedTenureMonths, setAcceptedTenureMonths] = useState(['3', '5'])
  const [taxProfileId, setTaxProfileId] = useState('')
  const [insuranceProfileId, setInsuranceProfileId] = useState('')
  const [allCategory, setAllCategory] = useState([])
  const [taxProfiles, setTaxProfiles] = useState([])
  const [insuranceProfiles, setInsuranceProfiles] = useState([])
  const [inStock, setInStock] = useState(true)

  const isEditing = Boolean(assetToEdit?.id);
  const isRecommendationApproval = Boolean(recommendationToApprove?.id);
  const selectedCategory = allCategory.find(cat => cat.name === assetType);
  const totalImageCount = images.length + retainedImages.length;
  const minimumImageCount = isRecommendationApproval ? 1 : 4;
  const canContinueAssetDetails = totalImageCount >= minimumImageCount && name && (assetType || isEditing) && totalUnits && description.length >= 7;
  const canSubmitPricing = unitPrice && markupRate && minDeposit && acceptedTenureMonths.length > 0 && taxProfileId && insuranceProfileId;

  const parseImageUrls = useCallback((imageValue) => {
    if (!imageValue) return [];
    if (Array.isArray(imageValue)) return imageValue;

    try {
      const parsedImages = JSON.parse(imageValue);
      return Array.isArray(parsedImages) ? parsedImages : [];
    } catch (error) {
      return [];
    }
  }, []);

  const resetForm = useCallback(() => {
    setName('');
    setDescription('');
    setUnitPrice('');
    setTotalUnits('');
    setMarkupRate('15');
    setMinDeposit('30');
    setAcceptedTenureMonths(['3', '5']);
    setTaxProfileId(taxProfiles[0]?.id ? String(taxProfiles[0].id) : '');
    setInsuranceProfileId(insuranceProfiles[0]?.id ? String(insuranceProfiles[0].id) : '');
    setAssetType('');
    setImages([]);
    setRetainedImages([]);
    setInStock(true);
    setActiveStep(0);
  }, [insuranceProfiles, taxProfiles]);

  const closeDrawer = () => {
    handleClose2();
    setActiveStep(0);
  };

  const handleNext = () => {
    if (activeStep === 0 && !canContinueAssetDetails) {
      toast.error(`Please complete asset details and upload at least ${minimumImageCount} image${minimumImageCount > 1 ? 's' : ''}`);
      return;
    }

    if (activeStep === 1 && !canSubmitPricing) {
      toast.error("Please complete pricing and cost details");
      return;
    }

    setActiveStep((currentStep) => Math.min(currentStep + 1, steps.length - 1));
  };

  const handleTenureChange = (index, month) => {
    setAcceptedTenureMonths((currentMonths) => {
      const updatedMonths = [...currentMonths];
      updatedMonths[index] = month;

      return [...new Set(updatedMonths)].sort((firstMonth, secondMonth) => Number(firstMonth) - Number(secondMonth));
    });
  };

  const addTenure = () => {
    const selectedTenures = acceptedTenureMonths.map((month) => Number(month));
    const highestTenure = Math.max(...selectedTenures, 0);
    const nextPreferredTenure = preferredTenureOptions.find((month) => (
      Number(month) > highestTenure && !acceptedTenureMonths.includes(month)
    ));
    const nextTenure = nextPreferredTenure || tenureOptions.find((month) => (
      Number(month) > highestTenure && !acceptedTenureMonths.includes(month)
    )) || tenureOptions.find((month) => !acceptedTenureMonths.includes(month));

    if (nextTenure) {
      setAcceptedTenureMonths((currentMonths) => [...currentMonths, nextTenure].sort((firstMonth, secondMonth) => Number(firstMonth) - Number(secondMonth)));
    }
  };

  const removeTenure = (month) => {
    setAcceptedTenureMonths((currentMonths) => currentMonths.filter((currentMonth) => currentMonth !== month));
  };

  const handleSubmit = (e) => {
    e.preventDefault()
    
    if (totalImageCount < minimumImageCount) {
      toast.error(`Please select at least ${minimumImageCount} image${minimumImageCount > 1 ? 's' : ''}`)
      return;
    }

    if (!canSubmitPricing) {
      toast.error("Please complete pricing and cost details");
      return;
    }
    
    setLoading(true)
    
    const formData = new FormData();
    
    images.forEach((image) => {
      formData.append(isEditing ? 'images' : 'images[]', image);
    });

    if (isEditing) {
      retainedImages.forEach((imageUrl) => {
        formData.append('retainedImages[]', imageUrl);
      });
    }
    
    formData.append('name', name);
    formData.append('description', description);
    formData.append('unitPrice', unitPrice);
    formData.append('totalUnits', totalUnits);
    formData.append('markupRatePercentage', markupRate);
    formData.append('minDepositPercentage', minDeposit);
    acceptedTenureMonths.forEach((tenureMonth) => {
      formData.append('acceptedTenureMonths[]', tenureMonth);
    });
    formData.append('taxProfileId', taxProfileId);
    formData.append('insuranceProfileId', insuranceProfileId);
    
    const categoryId = selectedCategory?.id || selectedCategory?._id || assetToEdit?.categoryId || assetToEdit?.category?.id;
    
    if (!categoryId && !isEditing) {
      toast.error("Please select a valid category");
      setLoading(false);
      return;
    }

    if (isEditing && categoryId) {
      formData.append('categoryId', categoryId);
    }
    
    const request = isRecommendationApproval
      ? postService.approveAssetRecommendation(recommendationToApprove.id, categoryId, formData)
      : isEditing
        ? putServices.updateAssetProduct(assetToEdit.id, formData)
        : postService.uploadAsset(categoryId, formData);

    request.then(
      (response) => {
          console.log('asset response ', response, response.data)
        setLoading(false)
        if (response.data.status === '2000') {
          toast.success(isRecommendationApproval ? "Recommendation approved successfully!!" : isEditing ? "Asset updated successfully!!" : "Asset added successfully!!");
          resetForm();
          if (onSaved) onSaved();
          handleClose2();
        } else {
          toast.error(response.data.description);
        }
      },
      (error) => {
        setLoading(false)
        toast.error(error.message);
      }
    )
  }

  useEffect(() => {
    setLoading(true)
    Promise.all([
      getServices.getAssestCategory(),
      getServices.getTaxProfiles(),
      getServices.getInsuranceProfiles(),
    ]).then(
      ([categoryResponse, taxResponse, insuranceResponse]) => {
        setLoading(false)

        if (categoryResponse.data.status === "2000") {
          setAllCategory(categoryResponse.data.data || [])
        } else {
          swal(categoryResponse.data.description)
        }

        if (taxResponse.data.status === "2000") {
          const activeTaxProfiles = (taxResponse.data.data || []).filter((profile) => profile.active);
          setTaxProfiles(activeTaxProfiles)
          setTaxProfileId(activeTaxProfiles[0]?.id ? String(activeTaxProfiles[0].id) : '')
        } else {
          swal(taxResponse.data.description)
        }

        if (insuranceResponse.data.status === "2000") {
          const activeInsuranceProfiles = (insuranceResponse.data.data || []).filter((profile) => profile.active);
          setInsuranceProfiles(activeInsuranceProfiles)
          setInsuranceProfileId(activeInsuranceProfiles[0]?.id ? String(activeInsuranceProfiles[0].id) : '')
        } else {
          swal(insuranceResponse.data.description)
        }
      },
      (error) => {
        setLoading(false)
        swal(error.message || 'Unable to fetch asset form data')
      }
    )
  }, [])

  useEffect(() => {
    if (!open2) return;

    if (recommendationToApprove) {
      setName(recommendationToApprove.requestedItemName || '');
      setDescription(recommendationToApprove.description || '');
      setUnitPrice('');
      setTotalUnits('');
      setMarkupRate('5');
      setMinDeposit('10');
      setAcceptedTenureMonths(['3', '6']);
      setTaxProfileId(taxProfiles[0]?.id ? String(taxProfiles[0].id) : '');
      setInsuranceProfileId(insuranceProfiles[0]?.id ? String(insuranceProfiles[0].id) : '');
      setAssetType('');
      setImages([]);
      setRetainedImages([]);
      setInStock(true);
      setActiveStep(0);
      return;
    }

    if (!assetToEdit) {
      resetForm();
      return;
    }

    setName(assetToEdit.name || assetToEdit.assetName || '');
    setDescription(assetToEdit.description || '');
    setUnitPrice(assetToEdit.unitPrice !== undefined && assetToEdit.unitPrice !== null ? String(assetToEdit.unitPrice) : '');
    setTotalUnits(assetToEdit.totalUnits !== undefined && assetToEdit.totalUnits !== null ? String(assetToEdit.totalUnits) : '');
    setMarkupRate(assetToEdit.markupRatePercentage !== undefined && assetToEdit.markupRatePercentage !== null ? String(assetToEdit.markupRatePercentage) : '15');
    setMinDeposit(assetToEdit.minDepositPercentage !== undefined && assetToEdit.minDepositPercentage !== null ? String(assetToEdit.minDepositPercentage) : '30');
    const existingTenures = (assetToEdit.acceptedTenureMonths || []).map((month) => String(month));
    setAcceptedTenureMonths(existingTenures.length > 0 ? existingTenures : ['3', '5']);
    setTaxProfileId(assetToEdit.taxProfile?.id ? String(assetToEdit.taxProfile.id) : '');
    setInsuranceProfileId(assetToEdit.insuranceProfile?.id ? String(assetToEdit.insuranceProfile.id) : '');
    setAssetType(assetToEdit.category?.name || assetToEdit.categoryName || '');
    setRetainedImages(parseImageUrls(assetToEdit.imagesUrl));
    setImages([]);
    setInStock((assetToEdit.status || assetToEdit.assetStatus || 'ACTIVE').toUpperCase() === 'ACTIVE' || assetToEdit.assetStatus === 'available');
    setActiveStep(0);
  }, [assetToEdit, recommendationToApprove, open2, parseImageUrls, resetForm, insuranceProfiles, taxProfiles])

  const renderInput = (label, value, onChange, placeholder = '', type = 'text') => (
    <div>
      <label className='mb-2 block text-sm md:text-base font-medium text-gray-700'>{label}</label>
      <input
        type={type}
        className='w-full bg-[#F7F8FB] outline-0 p-3 rounded-lg text-sm md:text-base placeholder:text-gray-400'
        placeholder={placeholder}
        value={value}
        required
        min={type === 'number' ? '0' : undefined}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(event) => {
          if (type === 'number' && event.key === '-') {
            event.preventDefault();
          }
        }}
      />
    </div>
  );

  const toNumber = (value) => Number(value || 0);
  const formatMoney = (value) => `NGN ${Math.round(toNumber(value)).toLocaleString()}`;
  const unitPriceAmount = toNumber(unitPrice);
  const downPaymentAmount = unitPriceAmount * (toNumber(minDeposit) / 100);
  const financedAmount = Math.max(unitPriceAmount - downPaymentAmount, 0);
  const markupAmount = financedAmount * (toNumber(markupRate) / 100);
  const taxProfileOptions = taxProfiles.map((profile) => ({
    value: String(profile.id),
    label: `${profile.name} (${profile.ratePercentage}%)`,
    percentage: Number(profile.ratePercentage || 0),
  }));
  const insuranceProfileOptions = insuranceProfiles.map((profile) => ({
    value: String(profile.id),
    label: `${profile.name} (${profile.ratePercentage}%)`,
    percentage: Number(profile.ratePercentage || 0),
  }));
  const selectedTax = taxProfileOptions.find((option) => option.value === taxProfileId);
  const selectedInsurance = insuranceProfileOptions.find((option) => option.value === insuranceProfileId);
  const taxAmount = unitPriceAmount * ((selectedTax?.percentage || 0) / 100);
  const insuranceAmount = unitPriceAmount * ((selectedInsurance?.percentage || 0) / 100);
  const totalCost = unitPriceAmount + markupAmount + taxAmount + insuranceAmount;
  const repaymentBalance = Math.max(totalCost - downPaymentAmount, 0);
  const primaryImageUrl = retainedImages[0] || (images[0] ? URL.createObjectURL(images[0]) : '');

  const renderSelect = (label, value, onChange, options) => (
    <div>
      <label className='mb-2 block text-sm md:text-base font-medium text-gray-700'>{label}</label>
      <div className='relative'>
        <select
          className='w-full appearance-none bg-white border outline-0 p-3 rounded-lg text-sm md:text-base h-[50px]'
          required
          value={value}
          onChange={(event) => onChange(event.target.value)}
        >
          {options.map((option) => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </select>
        <KeyboardArrowDownIcon className='absolute right-4 top-1/2 -translate-y-1/2 text-back-color2 pointer-events-none'/>
      </div>
    </div>
  );

  return (
    <div>
      <Drawer
        anchor='right'
        variant="temporary"
        open={open2}
        onClose={closeDrawer}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: 'block', sm: 'block' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: {
              xs: '100%',
              sm: '80%',
              md: drawerWidth
            },
            backgroundColor: 'white'
          },
        }}
      >
        <div className='px-4 h-full flex flex-col'>
          <div className='flex my-4 justify-between items-center px-2'>
            <h1 className='text-xl md:text-2xl font-bold text-back-color2'>{isRecommendationApproval ? 'Approve recommendation' : isEditing ? 'Edit asset' : 'Add new asset'}</h1>
            <div className='p-2 hover:bg-gray-100 rounded-full cursor-pointer' onClick={closeDrawer}>
              <CloseIcon />
            </div>
          </div>
          <div className='px-3 md:px-5 pt-4'>
            <div className='flex space-x-4 overflow-x-auto border-b border-gray-200'>
              {steps.map((step, index) => (
                <button
                  key={step}
                  type='button'
                  className={`px-4 py-2 -mb-px whitespace-nowrap rounded-t-lg font-normal text-[14px] inline-block text-back-color2 text-center border-b-2 ${activeStep === index ? 'border-primary-color' : 'border-transparent'}`}
                  onClick={() => setActiveStep(index)}
                >
                  {step}
                </button>
              ))}
            </div>
          </div>
          <div className='px-3 md:px-5 pt-3 flex-1 overflow-y-auto'>
            <ToastContainer/>
            <form id='add-asset-form' onSubmit={handleSubmit} className='mt-5 pb-20'>
              {activeStep === 0 && (
                <div className='space-y-5 pt-2'>
                  <div>
                    <label className='mb-2 block text-sm md:text-base font-medium text-gray-700'>Upload asset images</label>
                    <ImageUploader images={images} setImages={setImages} variant='asset' minimumText={`Upload minimum of ${minimumImageCount} image${minimumImageCount > 1 ? 's' : ''}`} />
                    {retainedImages.length > 0 && (
                      <div className='mt-5'>
                        <h4 className='mb-3 text-sm md:text-base font-medium text-text'>Retained images</h4>
                        <div className='grid grid-cols-2 sm:grid-cols-4 gap-4'>
                          {retainedImages.map((imageUrl, index) => (
                            <div className='relative overflow-hidden rounded-2xl bg-[#F4F5F8] aspect-square' key={`${imageUrl}-${index}`}>
                              <img src={imageUrl} className='h-full w-full object-cover' alt='Retained asset' />
                              <button
                                type='button'
                                className='absolute top-3 right-3 flex h-7 w-7 items-center justify-center rounded-full bg-text text-white'
                                onClick={() => setRetainedImages((currentImages) => currentImages.filter((_, imageIndex) => imageIndex !== index))}
                              >
                                x
                              </button>
                              {index === 0 && (
                                <div className='absolute bottom-0 left-0 right-0 bg-primary-color py-2 text-center text-sm font-bold text-white'>
                                  Primary
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  <div>
                    {renderInput('Asset name', name, setName, 'E.g PlayStation P5')}
                  </div>

                  <div>
                    <label className='mb-2 block text-sm md:text-base font-medium text-gray-700'>Asset Category</label>
                    <div className='relative'>
                      <select
                        className='w-full appearance-none bg-white border outline-0 p-3 rounded-lg text-sm md:text-base h-[50px]'
                        required
                        value={assetType}
                        onChange={(e) => setAssetType(e.target.value)}
                      >
                        <option value="">Select category</option>
                        {allCategory.map((category) => (
                          <option key={category.id || category._id || category.name} value={category.name}>
                            {category.name}
                          </option>
                        ))}
                      </select>
                      <KeyboardArrowDownIcon className='absolute right-4 top-1/2 -translate-y-1/2 text-back-color2 pointer-events-none'/>
                    </div>
                    {onCreateCategory ? (
                      <button type='button' onClick={onCreateCategory} className='mt-4 block ml-auto text-primary-color font-medium'>Create new product category</button>
                    ) : null}
                  </div>

                  <div>
                    {renderInput('Quantity', totalUnits, setTotalUnits, '10', 'number')}
                  </div>

                  <div>
                    <label className='mb-2 block text-sm md:text-base font-medium text-gray-700'>Stock status</label>
                    <button
                      type='button'
                      className='flex w-full items-center justify-between rounded-lg bg-[#F7F8FB] px-3 py-3 text-left'
                      onClick={() => setInStock((current) => !current)}
                    >
                      <span>
                        <span className='block text-[18px] text-primary-color'>{inStock ? 'In stock' : 'Out of stock'}</span>
                        <span className='mt-1 block text-sm text-primary-color'>{inStock ? 'Asset is available for users to request' : 'Asset is unavailable for user requests'}</span>
                      </span>
                      <span className={`relative h-8 w-14 rounded-full transition-colors ${inStock ? 'bg-[#344054]' : 'bg-gray-300'}`}>
                        <span className={`absolute top-1 h-6 w-6 rounded-full bg-white transition-transform ${inStock ? 'translate-x-7' : 'translate-x-1'}`}></span>
                      </span>
                    </button>
                  </div>

                  <div>
                    <label className='mb-2 block text-sm md:text-base font-medium text-gray-700'>Description</label>
                    <textarea
                      minLength={7}
                      className='border p-3 md:p-4 outline-0 rounded-lg w-full h-32 text-sm md:text-base focus:border-blue-500 transition-all resize-none'
                      placeholder='It is a Home based video game'
                      value={description}
                      required
                      onChange={(e) => setDescription(e.target.value)}
                    />
                  </div>
                </div>
              )}

              {activeStep === 1 && (
                <div className='space-y-5 pt-2'>
                  <div>
                    <label className='mb-2 block text-sm md:text-base font-medium text-gray-700'>Amount</label>
                    <div className='flex items-center rounded-lg bg-[#F7F8FB]'>
                      <span className='ml-3 rounded bg-[#E6E6E7] px-3 py-2 text-sm font-bold text-primary-color'>NGN</span>
                      <input
                        type='number'
                        className='w-full border-0 bg-transparent outline-none ring-0 p-3 text-sm md:text-base placeholder:text-gray-400 focus:border-0 focus:outline-none focus:ring-0'
                        placeholder='500,000'
                        value={unitPrice}
                        required
                        min='0'
                        onChange={(event) => setUnitPrice(event.target.value)}
                        onKeyDown={(event) => {
                          if (event.key === '-') {
                            event.preventDefault();
                          }
                        }}
                      />
                    </div>
                  </div>

                  <div>
                    {renderSelect(
                      'Set minimum down payment percentage',
                      minDeposit,
                      setMinDeposit,
                      downPaymentOptions.map((percentage) => ({
                        value: percentage,
                        label: `${percentage}%${percentage === '30' ? ' Recommended' : ''}`,
                      })),
                      '30% Recommended'
                    )}
                    <p className='mt-2 text-sm text-[#00B869]'>
                      User Pays {formatMoney(downPaymentAmount)} upfront ({minDeposit || 0}% of {formatMoney(unitPriceAmount)})
                    </p>
                  </div>

                  <div>
                    {renderSelect('Tax', taxProfileId, setTaxProfileId, taxProfileOptions, 'VAT(7.5%)')}
                  </div>

                  <div>
                    {renderSelect('Insurance', insuranceProfileId, setInsuranceProfileId, insuranceProfileOptions, 'Device Insurance')}
                  </div>

                  <div>
                    {renderSelect(
                      'Markup % (10-25%)',
                      markupRate,
                      setMarkupRate,
                      markupOptions.map((percentage) => ({ value: percentage, label: `${percentage}%` })),
                      '15%'
                    )}
                    <p className='mt-2 text-sm text-[#00B869]'>
                      HalalWealth pays {formatMoney(financedAmount)}, Markup = {formatMoney(markupAmount).replace('NGN ', '')}
                    </p>
                  </div>

                  <div>
                    <label className='mb-2 block text-sm md:text-base font-medium text-gray-700'>Repayment tenure options</label>
                    <div className='space-y-3'>
                      {acceptedTenureMonths.map((month, index) => {
                        const monthlyAmount = month ? repaymentBalance / Number(month) : 0;

                        return (
                          <div key={`${month}-${index}`} className='grid grid-cols-[1fr_auto_auto] items-center gap-3 rounded-lg bg-[#F7F8FB] p-3'>
                            <div className='relative'>
                              <select
                                className='w-full appearance-none rounded-lg border bg-white p-3 text-sm md:text-base h-[50px] outline-0'
                                value={month}
                                onChange={(event) => handleTenureChange(index, event.target.value)}
                              >
                                {tenureOptions.map((option) => (
                                  <option
                                    key={option}
                                    value={option}
                                    disabled={acceptedTenureMonths.includes(option) && option !== month}
                                  >
                                    {option} months
                                  </option>
                                ))}
                              </select>
                              <KeyboardArrowDownIcon className='absolute right-4 top-1/2 -translate-y-1/2 text-back-color2 pointer-events-none'/>
                            </div>
                            <p className='whitespace-nowrap text-sm text-[#00B869]'>
                              {unitPrice ? `${formatMoney(monthlyAmount)}/mo` : ''}
                            </p>
                            <button
                              type='button'
                              onClick={() => removeTenure(month)}
                              className='flex h-6 w-6 items-center justify-center rounded bg-text text-base leading-none text-white'
                            >
                              x
                            </button>
                          </div>
                        );
                      })}
                    </div>
                    <button type='button' onClick={addTenure} className='mt-4 block ml-auto text-primary-color font-medium'>
                      Add tenure
                    </button>
                  </div>
                </div>
              )}

              {activeStep === 2 && (
                <div className='space-y-5 pt-2'>
                  <div className='flex items-center gap-3 rounded-lg bg-[#F7F8FB] p-3'>
                    <div className='h-16 w-16 shrink-0 overflow-hidden rounded-lg bg-white'>
                      {primaryImageUrl ? (
                        <img src={primaryImageUrl} className='h-full w-full object-cover' alt='Primary asset preview' />
                      ) : null}
                    </div>
                    <div className='min-w-0 flex-1'>
                      <h2 className='truncate text-sm md:text-base font-bold text-black'>{name || 'Asset name'}</h2>
                      <p className='mt-1 text-sm text-gray-500'>{selectedCategory?.name || assetType || 'Product category'}</p>
                      <p className='mt-1 text-xs font-bold text-primary-color'>{totalUnits || 0} units available</p>
                    </div>
                    <span className='inline-flex shrink-0 items-center gap-2 rounded-lg bg-[#E6E6E7] px-2.5 py-1.5 text-xs font-bold text-primary-color'>
                      <span className={`h-2 w-2 rounded-full ${inStock ? 'bg-[#00B869]' : 'bg-gray-400'}`}></span>
                      {inStock ? 'In stock' : 'Out of stock'}
                    </span>
                  </div>

                  <div>
                    <p className='mb-2 block text-sm md:text-base font-medium text-gray-700'>Pricing overview</p>
                    <div className='overflow-hidden rounded-lg border'>
                      <div className='grid grid-cols-2 divide-x divide-gray-100'>
                        <div className='p-3'>
                          <p className='text-sm text-text'>Price</p>
                          <p className='mt-2 text-sm md:text-base font-bold text-black'>{unitPrice ? formatMoney(unitPriceAmount) : ''}</p>
                        </div>
                        <div className='p-3'>
                          <p className='text-sm text-text'>Down Payment</p>
                          <p className='mt-2 text-sm md:text-base font-bold text-black'>{unitPrice ? formatMoney(downPaymentAmount) : ''}</p>
                        </div>
                      </div>
                      <div className='flex items-center justify-between gap-3 border-t border-gray-100 p-3'>
                        <p className='text-sm text-text'>Tenure options</p>
                        <div className='flex flex-wrap justify-end gap-2'>
                          {acceptedTenureMonths.map((month) => (
                            <span key={`review-${month}`} className='rounded-full bg-purple-100 px-2.5 py-1 text-xs font-medium text-purple-700'>{month} mo</span>
                          ))}
                        </div>
                      </div>
                      <div className='flex items-center justify-between border-t border-gray-100 p-3'>
                        <p className='text-sm text-text'>Total cost</p>
                        <p className='text-sm md:text-base font-bold text-black'>{unitPrice ? formatMoney(totalCost) : ''}</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </form>
          </div>
          <div className='bg-white px-3 md:px-5'>
            <div className='flex my-8 justify-end border-t pt-6 mb-10 gap-3'>
              <button type='button' onClick={closeDrawer} className='rounded-lg bg-white border px-4 py-2 font-bold text-primary-color'>
                Discard
              </button>
              {activeStep < steps.length - 1 ? (
                <button type='button' onClick={handleNext} className='rounded-lg bg-violet-500 px-4 py-2 font-bold text-white'>
                  Next
                </button>
              ) : (
                <button type='button' onClick={handleSubmit} disabled={loading} className='rounded-lg bg-violet-500 px-4 py-2 font-bold text-white disabled:opacity-60'>
                  {loading ? 'Saving...' : isRecommendationApproval ? 'Approve request' : isEditing ? 'Save changes' : 'Add new asset'}
                </button>
              )}
            </div>
          </div>
        </div>
      </Drawer>
    </div>
  )
}

export default AddAssets
